/**
 * Single source of truth for the Hikvision event-webhook target.
 *
 * "Configurar webhook" and "Ler configuração" used to compute this independently: the
 * configurator knew about LPR cameras, the validator hardcoded the facial-recognition path
 * keyed by device id. A correctly configured LPR camera therefore always reported
 * "Configuração incorreta" — the validator was comparing against a URL nothing ever writes.
 *
 * Both now derive from here so they cannot drift apart again.
 */

/** Models whose firmware cannot do TLS — configured over plain HTTP instead. */
const HIKVISION_HTTP_ONLY_MODELS = ['DS-K1T341', 'DS-K1T342'];

export const WEBHOOK_HOST = 'access.panther.com.br';

/**
 * Transport the event webhook is configured with.
 *
 * This is deliberately a single constant rather than a per-call decision: "Configurar webhook"
 * has always written HTTP on port 80, so that is what every device in the field is running and
 * what events are actually arriving over. The validator previously expected HTTPS/443, which no
 * configuration path ever produced — so "Ler configuração" reported "Configuração incorreta" on
 * every Hikvision device, correctly configured or not.
 *
 * The cameras do advertise HTTPS support (protocolType opt="HTTP,HTTPS"), so moving to TLS is a
 * one-line change here — but it must be done deliberately and re-pushed to every device, not as
 * a side effect of fixing the indicator.
 */
export const EVENT_WEBHOOK_PROTOCOL: 'HTTP' | 'HTTPS' = 'HTTP';
export const EVENT_WEBHOOK_PORT = '80';

export interface HikDeviceLike {
  id?: string;
  device_type?: string;
  model?: string;
  config?: Record<string, any> | null;
}

export interface ExpectedHikWebhook {
  protocol: 'HTTP' | 'HTTPS';
  host: string;
  port: string;
  path: string;
}

export const isHikHTTPOnly = (model?: string): boolean =>
  !!model && HIKVISION_HTTP_ONLY_MODELS.some(m => model.toUpperCase().includes(m.toUpperCase()));

/** Both device_types reach the LPR path — see the generic "Tipo: LPR" tab in the device form. */
const isLprDevice = (deviceType?: string): boolean =>
  deviceType === 'hikvision_lpr' || deviceType === 'lpr';

/**
 * Where this device's events should be posted.
 *
 * LPR cameras post to the hikvision-lpr push endpoint keyed by the CAMERA id (the
 * hikvision_lpr_cameras row), not the iot_devices id — that is what iot-connect routes on.
 */
export function expectedHikWebhook(device: HikDeviceLike): ExpectedHikWebhook {
  const deviceId = device.id ?? '';

  const path = isLprDevice(device.device_type)
    ? `/api/v1/iot-connect/hikvision-lpr/${device.config?.hikvision_lpr_camera_id || deviceId}/push`
    : `/api/v1/iot-connect/facial-recognition/events/hikvision/${deviceId}`;

  return {
    protocol: EVENT_WEBHOOK_PROTOCOL,
    host: WEBHOOK_HOST,
    port: EVENT_WEBHOOK_PORT,
    path,
  };
}

/** The URL actually written to the device. Derived from expectedHikWebhook by construction. */
export function hikWebhookUrl(device: HikDeviceLike): string {
  const { protocol, host, port, path } = expectedHikWebhook(device);
  const isDefaultPort = (protocol === 'HTTPS' && port === '443') || (protocol === 'HTTP' && port === '80');
  const authority = isDefaultPort ? host : `${host}:${port}`;
  return `${protocol.toLowerCase()}://${authority}${path}`;
}
